import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SERVICE_OPTIONS, CONTACT } from "../data/site";
import { submitLead } from "../lib/api";
import { Eyebrow } from "./Reveal";

const empty = { name: "", company: "", email: "", phone: "", service: "", fleet_size: "", location: "", message: "" };
const inputCls =
  "rounded-none bg-mm-panel border-mm-green/20 text-white placeholder:text-gray-500 focus-visible:ring-mm-green focus-visible:ring-1 focus-visible:border-mm-green h-12";

export default function LeadForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in name, email, phone and select a service.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Request received. Our team will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request" className="bg-mm-base py-24 sm:py-32" data-testid="request-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        {/* Left: pitch + contact */}
        <div>
          <Eyebrow>Get In Touch</Eyebrow>
          <h2 className="mt-4 font-heading font-black uppercase tracking-tight text-white text-6xl sm:text-7xl lg:text-8xl leading-[0.82]">
            Get A <br /><span className="text-mm-green">Quote</span>
          </h2>
          <p className="mt-6 max-w-md text-gray-400 leading-relaxed">
            Tell us about your fleet and what you need. Your request goes straight to our sales
            team, we respond fast, 24/7/365, and deploy mobile units wherever you operate.
          </p>

          <div className="mt-10 space-y-5">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4">
              <span className="p-3 border border-mm-green/25 text-mm-green group-hover:bg-mm-green group-hover:text-black transition-colors"><Phone size={18} /></span>
              <span className="font-heading text-2xl text-white group-hover:text-mm-green transition-colors">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-4">
              <span className="p-3 border border-mm-green/25 text-mm-green group-hover:bg-mm-green group-hover:text-black transition-colors"><Mail size={18} /></span>
              <span className="font-body text-lg text-gray-300 group-hover:text-mm-green transition-colors break-all">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="p-3 border border-mm-green/25 text-mm-green"><MapPin size={18} /></span>
              <span className="font-body text-lg text-gray-300">{CONTACT.address}</span>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="beam p-6 sm:p-8"
        >
          {done ? (
            <div className="relative z-10 min-h-[420px] flex flex-col items-center justify-center text-center" data-testid="lead-success">
              <CheckCircle2 size={64} className="text-mm-green" />
              <h3 className="mt-6 font-heading font-bold uppercase text-3xl text-white">Request Received</h3>
              <p className="mt-3 text-gray-400 max-w-sm">Thanks, our team will reach out shortly. For urgent breakdowns, call {CONTACT.phone}.</p>
              <button onClick={() => setDone(false)} className="mt-8 font-heading uppercase tracking-wide text-mm-green border border-mm-green/40 px-6 py-3 hover:bg-mm-green hover:text-black transition-colors">
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="relative z-10 space-y-5" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *"><Input data-testid="lead-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="John Smith" /></Field>
                <Field label="Company"><Input data-testid="lead-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Acme Logistics" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Email *"><Input data-testid="lead-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" /></Field>
                <Field label="Phone *"><Input data-testid="lead-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(325) 000-0000" /></Field>
              </div>
              <Field label="Service Needed *">
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="lead-service" className={`${inputCls} w-full`}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-mm-panel border-mm-green/20 text-white">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} className="focus:bg-mm-green focus:text-black">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Fleet Size"><Input data-testid="lead-fleet" className={inputCls} value={form.fleet_size} onChange={set("fleet_size")} placeholder="e.g. 25 units" /></Field>
                <Field label="Location"><Input data-testid="lead-location" className={inputCls} value={form.location} onChange={set("location")} placeholder="City, State" /></Field>
              </div>
              <Field label="Message">
                <Textarea data-testid="lead-message" className={`${inputCls} h-28`} value={form.message} onChange={set("message")} placeholder="Tell us what you need..." />
              </Field>
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-submit"
                className="w-full font-heading font-bold uppercase tracking-wider text-lg bg-mm-green text-black py-4 hover:bg-white transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="animate-spin" size={20} /> Sending...</> : "Submit Quote Request"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-mono text-[11px] tracking-[0.15em] uppercase text-gray-400">{label}</Label>
    {children}
  </div>
);
